/**
 * VerificationController - DOM Bridge for AppBouncer SDK
 * Bridges the HTML UI with the AppBouncer SDK functionality
 */

import { AppBouncer, AppBouncerCallbacks, AppBouncerConfig } from '../orchestration';
import { ScreenConfig, ScreenName, VerificationMethod, VerificationResult } from '../orchestration';
import { SceneQualityResult } from '../face-detection';
import { RequirementsData } from './requirements';
import { getMockBackend, MockBackend } from '../testing';
import { ThemeService, ThemePreference } from '../services/theme-service';
import { i18n, Language } from '../i18n';

// ============================================================
// Types
// ============================================================

export interface VerificationControllerConfig {
  /** Video element for camera feed */
  videoElement: HTMLVideoElement;
  /** Canvas element for overlays (optional) */
  overlayCanvas?: HTMLCanvasElement;
  /** Container for screen content */
  screenContainer: HTMLElement;
  /** Status message element */
  statusElement?: HTMLElement;
  /** Progress indicator element */
  progressElement?: HTMLElement;
  /** API base URL (defaults to current origin) */
  apiBaseUrl?: string;
  /** Assets base URL for static files like models (optional, defaults to relative paths) */
  assetsBaseUrl?: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Pre-fetched requirements (skips SDK's own fetch if provided) */
  prefetchedRequirements?: RequirementsData;
  /**
   * Allowed callback URL origins for redirect (security).
   * If not specified, only HTTPS URLs are allowed.
   * Supports wildcards: ['*.example.com', 'https://app.mysite.com']
   */
  allowedCallbackOrigins?: string[];
  /**
   * Allow localhost callbacks for development (default: true).
   * Set to false in production for stricter security.
   */
  allowLocalhostCallbacks?: boolean;
}

export interface URLParams {
  apiKey: string | null;
  callbackUrl: string | null;
  mode?: string | null;
  /** External user identifier for tracking verification across sessions */
  userId?: string | null;
  /** Theme preference: 'light', 'dark', or 'system' (OS preference) */
  theme?: 'light' | 'dark' | 'system' | null;
  /** Locale/language code (e.g., 'en', 'de', 'es') or 'auto' for browser detection */
  locale?: string | null;
}

export interface VerificationControllerCallbacks {
  /** Called when SDK is ready */
  onReady?: () => void;
  /** Called when requirements are loaded */
  onRequirementsLoaded?: (requirements: RequirementsData) => void;
  /** Called when screen changes */
  onScreenChange?: (screen: ScreenConfig, prevScreen?: ScreenName) => void;
  /** Called on verification complete */
  onComplete?: (result: VerificationResult) => void;
  /** Called on error */
  onError?: (error: Error, context?: string) => void;
  /** Called with status messages */
  onStatus?: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void;
  /** Called when face detection state changes */
  onFaceDetected?: (detected: boolean, quality?: SceneQualityResult) => void;
  /** Called when liveness action changes (action name and user-friendly hint) */
  onLivenessAction?: (action: string, hint: string, stepIndex: number, totalSteps: number) => void;
}

// ============================================================
// VerificationController Class
// ============================================================

export class VerificationController {
  private config: VerificationControllerConfig;
  private callbacks: VerificationControllerCallbacks = {};
  private sdk: AppBouncer | null = null;
  private urlParams: URLParams;
  private screenElements: Map<ScreenName, HTMLElement> = new Map();
  private currentScreen: ScreenName | null = null;

  // Mock backend support
  private useMockBackend = false;
  private mockBackend: MockBackend | null = null;

  // Face detection loop
  private faceDetectionInterval: number | null = null;
  private isFacePositioned = false;

  // Current liveness action tracking
  private currentLivenessAction: string | null = null;
  private livenessActionIndex = 0;
  private isLivenessRunning = false;

  // Theme service
  private themeService: ThemeService;

  // i18n auto-translate unsubscribe function
  private i18nUnsubscribe: (() => void) | null = null;

  // Session ID from backend (for redirect back to external SDK)
  private sessionId: string | null = null;

  constructor(config: VerificationControllerConfig) {
    this.config = config;
    this.urlParams = this.parseUrlParams();

    // Initialize theme service — URL param takes priority, then localStorage, then 'system'
    const themeFromUrl = this.resolveThemePreference(this.urlParams.theme);
    this.themeService = new ThemeService({
      ...(themeFromUrl ? { preference: themeFromUrl } : {}),
      debug: config.debug,
    });
    this.themeService.initialize();
    this.log(
      'Theme initialized:',
      themeFromUrl ?? '(from storage)',
      '-> resolved:',
      this.themeService.getResolvedTheme()
    );

    // Initialize locale from URL params
    this.initializeLocale(this.urlParams.locale);

    // Enable mock backend for demo mode
    if (this.urlParams.apiKey === 'demo' || this.urlParams.mode === 'demo') {
      this.useMockBackend = true;
      this.mockBackend = getMockBackend({ debug: config.debug });
      this.log('Mock backend enabled for demo mode');
    }
  }

  /**
   * Resolve theme preference from URL param.
   * Returns undefined when no explicit URL param, so ThemeService falls back to localStorage.
   */
  private resolveThemePreference(
    themeParam: string | null | undefined
  ): ThemePreference | undefined {
    if (themeParam === 'light' || themeParam === 'dark' || themeParam === 'system') {
      return themeParam;
    }
    return undefined;
  }

  /**
   * Initialize locale/language from URL param.
   * Supports specific language codes or 'auto' for browser detection.
   * Enables auto-translation of DOM elements with data-i18n attributes.
   */
  private initializeLocale(localeParam: string | null | undefined): void {
    if (!localeParam || localeParam === 'auto') {
      // Auto-detect from browser
      const detected = i18n.detectBrowserLanguage();
      this.log('Locale auto-detected:', detected);
    } else if (i18n.isSupported(localeParam)) {
      // Try to set the specific locale
      i18n.setLanguage(localeParam as Language);
      this.log('Locale set from URL:', localeParam);
    } else {
      this.log('Locale not supported, using default:', localeParam);
      i18n.setLanguage('en');
    }

    // Enable auto-translate for the screen container
    // This translates all data-i18n elements and re-translates on language change
    this.i18nUnsubscribe = i18n.enableAutoTranslate(this.config.screenContainer);
    this.log('Auto-translate enabled for locale:', i18n.getLanguage());
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Initialize the controller and SDK
   */
  async initialize(): Promise<void> {
    // In demo mode, api_key is optional (defaults to 'demo' in parseUrlParams)
    // Only require api_key for non-demo mode
    if (!this.urlParams.apiKey && this.urlParams.mode !== 'demo') {
      throw new Error('Missing required URL parameter: api_key');
    }

    // Discover screen elements from DOM
    this.discoverScreenElements();

    // Create SDK configuration
    // In demo mode, apiKey defaults to 'demo' in parseUrlParams
    const sdkConfig: AppBouncerConfig = {
      apiBaseUrl: this.config.apiBaseUrl || window.location.origin,
      assetsBaseUrl: this.config.assetsBaseUrl,
      apiKey: this.urlParams.apiKey || 'demo',
      debug: this.config.debug,
    };

    // Create SDK callbacks
    const sdkCallbacks: AppBouncerCallbacks = {
      onReady: () => {
        this.log('SDK ready');
        this.callbacks.onReady?.();
      },
      onScreenChange: (screen, prev) => {
        this.handleScreenChange(screen, prev);
        this.callbacks.onScreenChange?.(screen, prev);
      },
      onComplete: (result) => {
        this.handleVerificationComplete(result);
        this.callbacks.onComplete?.(result);
      },
      onError: (error, context) => {
        this.handleError(error, context);
        this.callbacks.onError?.(error, context);
      },
      onStatus: (message, type) => {
        // Forward to external callback only — host app decides what/when to display
        this.callbacks.onStatus?.(message, type);
      },
      onFaceDetected: (detected, quality) => {
        this.handleFaceDetection(detected, quality);
      },
      onLivenessAction: (action, instruction, stepIndex, totalSteps) => {
        // Track current action for UI state
        this.currentLivenessAction = action;

        // Forward to controller callback with step info from the detector
        this.callbacks.onLivenessAction?.(
          action,
          instruction,
          stepIndex ?? this.livenessActionIndex,
          totalSteps ?? 2
        );

        // Increment step index for next action
        this.livenessActionIndex++;

        this.log(
          `Liveness action: ${action} (step ${this.livenessActionIndex}/${totalSteps ?? 1})`
        );
      },
      onAgeEstimate: (age, confidence) => {
        this.log(`Age estimate: ${age} (confidence: ${confidence})`);
      },
    };

    // Initialize SDK
    this.sdk = new AppBouncer(sdkConfig);
    this.sdk.setCallbacks(sdkCallbacks);

    await this.sdk.initialize(this.config.videoElement, this.config.overlayCanvas);

    // Set up dynamic flow from requirements
    // If prefetched requirements provided, use those (skip duplicate fetch)
    console.log(
      '[VerificationController] prefetchedRequirements =',
      this.config.prefetchedRequirements
    );
    if (this.config.prefetchedRequirements) {
      console.log('[VerificationController] USING PREFETCHED - skipping fetch');
      this.log('Using prefetched requirements:', this.config.prefetchedRequirements.methods);
      this.sdk.setFlowFromMethods(this.config.prefetchedRequirements.methods);
      this.callbacks.onRequirementsLoaded?.(this.config.prefetchedRequirements);
    } else if (this.useMockBackend && this.mockBackend) {
      // Demo mode: use mock backend for requirements (no real API call)
      console.log('[VerificationController] DEMO MODE - using mock requirements');
      try {
        const mockResponse = await this.mockBackend.checkRequirements(
          this.urlParams.apiKey || 'demo'
        );
        const requirements: RequirementsData = mockResponse.data;
        this.sdk.setFlowFromMethods(requirements.methods);
        this.callbacks.onRequirementsLoaded?.(requirements);
        this.log('Mock requirements loaded:', requirements.methods);
      } catch (error) {
        console.error('[VerificationController] Mock requirements failed:', error);
        this.log('Using default demo flow');
        // Use default demo flow: liveness + age
        this.sdk.setFlowFromMethods(['liveness', 'age']);
      }
    } else {
      // Fetch requirements from backend
      console.log('[VerificationController] NO PREFETCHED - will fetch from backend');
      try {
        // Pass userId from URL params for session tracking
        const requirements = await this.sdk.fetchRequirements(undefined, this.urlParams.userId);
        this.callbacks.onRequirementsLoaded?.(requirements);
        this.log('Requirements loaded successfully:', requirements.methods);

        // Store session_id from backend for redirect back to external SDK
        if (requirements.session_id) {
          this.sessionId = requirements.session_id;
          this.log('Session ID from backend:', this.sessionId);
        }
      } catch (error) {
        // Log the actual error for debugging - this is a common failure point
        console.error('[VerificationController] Failed to fetch requirements:', error);
        this.log(
          'Failed to fetch requirements, using default flow. Error:',
          error instanceof Error ? error.message : String(error)
        );
        // Note: requiredMethods stays empty [], using default flow
      }
    }
  }

  /**
   * Set callbacks
   */
  setCallbacks(callbacks: VerificationControllerCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Start verification flow
   */
  start(): void {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }
    this.sdk.start();
  }

  /**
   * Navigate to next screen
   */
  next(): void {
    this.sdk?.goNext();
  }

  /**
   * Navigate to previous screen
   */
  back(): void {
    this.sdk?.goBack();
  }

  /**
   * Navigate to specific screen
   */
  goTo(screen: ScreenName): void {
    this.sdk?.goToScreen(screen);
  }

  /**
   * Cancel verification
   */
  cancel(): void {
    this.sdk?.cancel();
    this.redirectToCallback('cancelled');
  }

  /**
   * Get SDK instance
   */
  getSDK(): AppBouncer | null {
    return this.sdk;
  }

  /**
   * Get URL parameters
   */
  getUrlParams(): URLParams {
    return { ...this.urlParams };
  }

  /**
   * Check if using mock backend
   */
  isUsingMockBackend(): boolean {
    return this.useMockBackend;
  }

  // ============================================================
  // Camera and Face Detection Methods
  // ============================================================

  /**
   * Start camera and show video element
   */
  async startCamera(): Promise<boolean> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      // Show video element
      this.config.videoElement.classList.remove('hidden');

      // Request camera access through SDK
      const success = await this.sdk.requestCameraAccess();

      if (success) {
        // Show overlay canvas if present
        if (this.config.overlayCanvas) {
          this.config.overlayCanvas.classList.remove('hidden');
        }
        this.log('Camera started successfully');
        this.callbacks.onStatus?.('Camera ready', 'success');
      } else {
        this.callbacks.onStatus?.('Camera access denied', 'error');
      }

      return success;
    } catch (error) {
      this.log('Camera start error:', error);
      this.callbacks.onStatus?.('Camera error', 'error');
      return false;
    }
  }

  /**
   * Stop camera and hide video element
   */
  stopCamera(): void {
    this.stopFaceDetectionLoop();
    this.config.videoElement.classList.add('hidden');
    if (this.config.overlayCanvas) {
      this.config.overlayCanvas.classList.add('hidden');
    }
  }

  /**
   * Start face detection loop.
   * This continuously detects faces and updates lastFaceData which the
   * liveness detector pulls via getLandmarks() callback.
   * Uses requestAnimationFrame for ~60 FPS to catch quick actions like blinks.
   */
  startFaceDetectionLoop(): void {
    if (this.faceDetectionInterval) {
      return; // Already running
    }

    this.log('Starting face detection loop (requestAnimationFrame)');

    const detect = async () => {
      if (!this.sdk || this.faceDetectionInterval === null) return;

      try {
        const positioned = await this.sdk.checkFacePosition();
        this.isFacePositioned = positioned;
      } catch (error) {
        this.log('Face detection error:', error);
      }

      // Continue loop using requestAnimationFrame for ~60 FPS
      // Critical for detecting quick actions like blinks (150-400ms)
      if (this.faceDetectionInterval !== null) {
        this.faceDetectionInterval = window.requestAnimationFrame(detect);
      }
    };

    // Start the loop
    this.faceDetectionInterval = window.requestAnimationFrame(detect);
  }

  /**
   * Stop face detection loop
   */
  stopFaceDetectionLoop(): void {
    if (this.faceDetectionInterval) {
      window.cancelAnimationFrame(this.faceDetectionInterval);
      this.faceDetectionInterval = null;
      this.log('Face detection loop stopped');
    }
  }

  /**
   * Check if face is currently positioned correctly
   */
  isFaceReady(): boolean {
    return this.isFacePositioned;
  }

  // ============================================================
  // Liveness Flow Methods
  // ============================================================

  /**
   * Run the complete liveness verification flow.
   * This orchestrates getting challenge, running actions, and submitting results.
   */
  async runLivenessFlow(): Promise<boolean> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    // Prevent multiple concurrent liveness flows
    if (this.isLivenessRunning) {
      this.log('Liveness flow already running, ignoring duplicate call');
      return false;
    }

    this.isLivenessRunning = true;
    this.log('Starting liveness flow');

    try {
      // Ensure face detection loop is running to feed landmarks
      this.startFaceDetectionLoop();

      // Use mock backend for demo mode (no real API calls)
      if (this.useMockBackend && this.mockBackend) {
        this.log('Running liveness with mock backend');
        const success = await this.runMockLivenessFlow();
        this.stopFaceDetectionLoop();
        this.isLivenessRunning = false;
        return success;
      }

      // Run liveness through SDK (handles challenge fetch, actions, verification)
      const success = await this.sdk.runLiveness();

      // Stop loop after liveness completes
      this.stopFaceDetectionLoop();
      this.isLivenessRunning = false;

      return success;
    } catch (error) {
      this.log('Liveness flow error:', error);
      this.stopFaceDetectionLoop();
      this.isLivenessRunning = false;
      return false;
    }
  }

  /**
   * Run liveness flow using mock backend (for demo mode).
   * Now just uses the simplified offline liveness detector.
   */
  private async runMockLivenessFlow(): Promise<boolean> {
    if (!this.sdk) {
      return false;
    }

    try {
      // Get liveness detector from SDK
      const livenessDetector = this.sdk.getLivenessDetector();
      if (!livenessDetector) {
        throw new Error('Liveness detector not available');
      }

      // Reset step tracking before starting
      this.livenessActionIndex = 0;

      // Run simplified offline liveness (picks 2 random actions)
      // SDK's callbacks are already set up during initialize() and will
      // forward to onLivenessAction which we handle in the SDK callback setup
      const result = await livenessDetector.verify();
      this.log('Liveness result:', result);

      // Clear current action on completion
      this.currentLivenessAction = null;

      return result.success;
    } catch (error) {
      this.log('Mock liveness flow error:', error);
      return false;
    }
  }

  /**
   * Pause liveness detection for a given reason.
   */
  pauseLiveness(reason: string = 'lighting'): void {
    this.sdk?.pauseLiveness(reason);
  }

  /**
   * Remove a pause reason. Only resumes if no other reasons remain.
   */
  resumeLiveness(reason: string = 'lighting'): void {
    this.sdk?.resumeLiveness(reason);
  }

  /**
   * Check if liveness is paused for a specific reason.
   */
  isLivenessPausedForReason(reason: string): boolean {
    return this.sdk?.isLivenessPausedForReason(reason) ?? false;
  }

  /**
   * Check if liveness is paused for ANY reason.
   */
  isLivenessPaused(): boolean {
    return this.sdk?.isLivenessPaused() ?? false;
  }

  /**
   * Reset liveness state for retry.
   * Call this before retrying liveness verification.
   */
  resetLiveness(): void {
    this.log('Resetting liveness state');

    // Reset controller state
    this.currentLivenessAction = null;
    this.livenessActionIndex = 0;
    this.isLivenessRunning = false;

    // Reset SDK liveness detector
    if (this.sdk) {
      this.sdk.resetLivenessDetector();
    }
  }

  /**
   * Estimate age from threshold probabilities using linear interpolation.
   * Maps probabilities to approximate age for backward compatibility.
   */
  private estimateAgeFromProbabilities(probs: Record<string, number>): number {
    const thresholds = [
      { age: 12, key: '12+' },
      { age: 15, key: '15+' },
      { age: 18, key: '18+' },
      { age: 21, key: '21+' },
      { age: 25, key: '25+' },
    ];

    // Find where probability drops below 50% (transition point)
    for (let i = 0; i < thresholds.length; i++) {
      const prob = probs[thresholds[i].key] ?? 0;
      if (prob < 0.5) {
        if (i === 0) return 10;
        const prevProb = probs[thresholds[i - 1].key] ?? 1;
        const ratio = (0.5 - prob) / (prevProb - prob);
        return thresholds[i - 1].age + ratio * (thresholds[i].age - thresholds[i - 1].age);
      }
    }

    // All probabilities >= 50%, estimate based on 25+ probability
    const prob25 = probs['25+'] ?? 0.5;
    return 25 + (prob25 - 0.5) * 30;
  }

  /**
   * Estimate age from current frame (single estimate, fast)
   */
  async estimateAge(): Promise<{ age: number; confidence: number } | null> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      const result = await this.sdk.estimateAge();
      if (!result) return null;

      // Derive age from verification probabilities
      const age = this.estimateAgeFromProbabilities(result.allProbabilities);
      return { age: Math.round(age), confidence: result.confidence };
    } catch (error) {
      this.log('Age estimation error:', error);
      return null;
    }
  }

  /**
   * Wait for the user's face to be properly centered in the frame.
   * Polls checkFacePosition() until it returns true or timeout.
   *
   * Use this before age estimation to ensure the face is properly positioned
   * (e.g., after a liveness "turn left" action).
   *
   * @param maxWaitMs - Maximum time to wait (default: 10000ms)
   * @param checkIntervalMs - Polling interval (default: 300ms)
   * @returns true if face is centered within timeout, false otherwise
   */
  async waitForCenteredFace(
    maxWaitMs: number = 10000,
    checkIntervalMs: number = 300
  ): Promise<boolean> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    const startTime = Date.now();
    let lastStatus = '';

    this.log('Waiting for face to center...');

    while (Date.now() - startTime < maxWaitMs) {
      try {
        const isCentered = await this.sdk.checkFacePosition();

        if (isCentered) {
          this.log('Face is now centered');
          this.callbacks.onStatus?.('Face centered - starting analysis', 'success');
          return true;
        }

        // Only update status if it changed (avoid spamming)
        const newStatus = 'Please center your face...';
        if (newStatus !== lastStatus) {
          this.callbacks.onStatus?.(newStatus, 'info');
          lastStatus = newStatus;
        }
      } catch (error) {
        this.log('Face position check error:', error);
      }

      await new Promise((r) => setTimeout(r, checkIntervalMs));
    }

    this.log('Timeout waiting for face to center');
    return false;
  }

  /**
   * Estimate age with multiple samples and averaging.
   * Takes 4-5 seconds and shows progress ("Analyzing face 3 of 8...").
   * Filters low-confidence estimates before averaging for more accurate results.
   * Returns the best image closest to the average for upload.
   *
   * IMPORTANT: This method now waits for the face to be properly centered
   * before starting estimation. This ensures accurate results after liveness
   * actions like "turn left/right" that leave the face off-center.
   *
   * @param onProgress - Optional callback for progress updates
   * @returns Averaged age estimate with confidence and best image
   */
  async estimateAgeMultiple(onProgress?: (current: number, total: number) => void): Promise<{
    age: number;
    confidence: number;
    bestImage: string | null;
    allProbabilities: Record<string, number>;
    minAge: number;
    verified: boolean;
    passRate?: number;
    passedCount?: number;
    estimatesUsed: number;
  } | null> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      // Wait for face to be centered before starting age estimation
      this.callbacks.onStatus?.(
        i18n.t('face.centerInFrame', 'Place your head within the frame to continue...'),
        'info'
      );
      const isCentered = await this.waitForCenteredFace(10000);

      if (!isCentered) {
        this.callbacks.onStatus?.(
          i18n.t('face.noFace', 'Could not detect centered face. Please try again.'),
          'warning'
        );
        return null;
      }

      this.callbacks.onStatus?.(
        i18n.t('screens.ageEstimation.analyzing', 'Starting age estimation...'),
        'info'
      );

      const result = await this.sdk.estimateAgeMultiple({
        numEstimates: 4,
        delayBetweenMs: 600,
        confidenceThreshold: 0.6,
        onProgress: (current, total) => {
          onProgress?.(current, total);
          // Status is already updated by AppBouncer
        },
      });

      if (!result) return null;

      // Derive age from verification probabilities
      const age = this.estimateAgeFromProbabilities(result.allProbabilities);
      return {
        age: Math.round(age),
        confidence: result.confidence,
        bestImage: result.bestImage,
        allProbabilities: result.allProbabilities,
        minAge: result.minAge,
        verified: result.verified,
        passRate: result.passRate,
        passedCount: result.passedCount,
        estimatesUsed: result.estimatesUsed,
      };
    } catch (error) {
      this.log('Multi-estimate age estimation error:', error);
      this.callbacks.onStatus?.('Age estimation failed', 'error');
      return null;
    }
  }

  /**
   * Set OCR extracted age from document
   */
  setOcrAge(age: number): void {
    if (!this.sdk) return;
    this.sdk.setOcrAge(age);
    this.log('OCR age set:', age);
  }

  /**
   * Set estimated age from ONNX model
   */
  setEstimatedAge(age: number): void {
    if (!this.sdk) return;
    this.sdk.setEstimatedAge(age);
    this.log('Estimated age set:', age);
  }

  /**
   * Set best image for upload
   */
  setBestImage(imageDataUrl: string | null): void {
    if (!this.sdk) return;
    this.sdk.setBestImage(imageDataUrl);
    this.log('Best image set:', imageDataUrl ? 'yes' : 'no');
  }

  /**
   * Set user consent for data collection
   */
  setConsentGiven(consent: boolean | null): void {
    if (!this.sdk) return;
    this.sdk.setConsentGiven(consent);
    this.log('Consent given:', consent);
  }

  /**
   * Check if a verification method is required
   */
  isMethodRequired(method: VerificationMethod): boolean {
    if (!this.sdk) return false;
    return this.sdk.isMethodRequired(method);
  }

  /**
   * Manually set the verification flow from methods.
   * Use this when requirements were fetched externally (e.g., by the HTML)
   * but the SDK's internal fetchRequirements() failed.
   *
   * @param methods - Array of verification method names
   */
  setFlowFromMethods(methods: VerificationMethod[]): void {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }
    this.sdk.setFlowFromMethods(methods);
    this.log('Flow set from external methods:', methods);
  }

  /**
   * Get the required methods currently configured in the SDK.
   * Returns empty array if no methods have been set.
   */
  getRequiredMethods(): string[] {
    if (!this.sdk) return [];
    return this.sdk.getRequiredMethods();
  }

  /**
   * Get the liveness result from the current session.
   * Use this after runLivenessFlow() to get the actual action results
   * for submission to the backend.
   *
   * @returns Liveness result with passed status and action results, or null
   */
  getLivenessResult(): {
    passed: boolean;
    score?: number;
    actions?: Array<{ action: string; ok: boolean }>;
  } | null {
    if (!this.sdk) return null;
    return this.sdk.getLivenessResult() ?? null;
  }

  /**
   * Run face comparison for anti-spoofing check.
   * Compares selfies captured at different stages to detect face swaps.
   * Call this before completing verification.
   *
   * @returns Face comparison result with passed status and similarity score
   */
  runFaceComparison(): {
    passed: boolean;
    averageSimilarity: number;
    captureCount: number;
    error?: string;
  } | null {
    if (!this.sdk) return null;
    return this.sdk.runFaceComparison();
  }

  /**
   * Upload the best age estimation image to the backend.
   * Call this after estimateAgeMultiple() with the bestImage from the result.
   *
   * @param imageDataUrl - The image data URL (JPEG) to upload
   * @returns Upload result with the stored image URL
   */
  async uploadAgeImage(imageDataUrl: string): Promise<{ url: string } | null> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      this.callbacks.onStatus?.('Uploading age image...', 'info');
      const result = await this.sdk.uploadAgeImage(imageDataUrl);

      if (result) {
        this.log('Age image uploaded:', result.url);
        this.callbacks.onStatus?.('Age image uploaded', 'success');
      }

      return result;
    } catch (error) {
      this.log('Age image upload error:', error);
      this.callbacks.onStatus?.('Failed to upload age image', 'error');
      return null;
    }
  }

  // ============================================================
  // Document Consent Methods
  // ============================================================

  /**
   * Handle user allowing document/selfie storage.
   * Call this when user clicks "Allow" on the consent modal.
   * Proceeds to document capture flow.
   */
  handleConsentAllow(): void {
    if (!this.sdk) return;

    this.sdk.setConsentGiven(true);
    this.log('User consented to document storage');

    // Proceed to next screen (document_select)
    this.sdk.goNext();
  }

  /**
   * Handle user declining document/selfie storage.
   * Call this when user clicks "Decline" on the consent modal.
   * Rejects verification and redirects to callback with failure.
   */
  handleConsentDecline(): void {
    if (!this.sdk) return;

    this.sdk.setConsentGiven(false);
    this.log('User declined document storage - rejecting verification');

    // Update status
    this.callbacks.onStatus?.('Verification declined', 'info');

    // Redirect to callback with failed status
    this.redirectToCallback('failed');
  }

  // ============================================================
  // Document Capture Methods
  // ============================================================

  /**
   * Capture document front
   */
  async captureDocumentFront(): Promise<boolean> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      const doc = await this.sdk.captureDocument('front');
      return doc !== null;
    } catch (error) {
      this.log('Document capture error:', error);
      return false;
    }
  }

  /**
   * Capture document back
   */
  async captureDocumentBack(): Promise<boolean> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      const doc = await this.sdk.captureDocument('back');
      return doc !== null;
    } catch (error) {
      this.log('Document capture error:', error);
      return false;
    }
  }

  /**
   * Upload documents and poll for OCR results
   */
  async uploadDocuments(): Promise<boolean> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    try {
      return await this.sdk.uploadDocuments();
    } catch (error) {
      this.log('Document upload error:', error);
      return false;
    }
  }

  // ============================================================
  // Verification Completion
  // ============================================================

  /**
   * Complete verification and submit to server
   */
  async completeVerification(): Promise<VerificationResult> {
    if (!this.sdk) {
      throw new Error('Controller not initialized');
    }

    return await this.sdk.completeVerification();
  }

  /**
   * Dispose controller and SDK
   */
  dispose(): void {
    this.stopFaceDetectionLoop();
    this.themeService.dispose();
    this.i18nUnsubscribe?.();
    this.i18nUnsubscribe = null;
    this.sdk?.dispose();
    this.sdk = null;
    this.screenElements.clear();
  }

  // ============================================================
  // Theme and Locale Methods
  // ============================================================

  /**
   * Get the theme service instance.
   * Use this to access theme-related functionality.
   */
  getThemeService(): ThemeService {
    return this.themeService;
  }

  /**
   * Get the current resolved theme ('light' or 'dark').
   */
  getTheme(): 'light' | 'dark' {
    return this.themeService.getResolvedTheme();
  }

  /**
   * Set theme preference.
   * @param preference - 'light', 'dark', or 'system'
   */
  setTheme(preference: ThemePreference): void {
    this.themeService.setPreference(preference);
    this.log('Theme preference changed:', preference);
  }

  /**
   * Get the current language code.
   */
  getLocale(): string {
    return i18n.getLanguage();
  }

  /**
   * Set the language/locale.
   * @param locale - Language code (e.g., 'en', 'de', 'es')
   */
  setLocale(locale: string): void {
    if (i18n.isSupported(locale)) {
      i18n.setLanguage(locale as Language);
      this.log('Locale changed:', locale);
    } else {
      this.log('Locale not supported:', locale);
    }
  }

  /**
   * Get available languages.
   */
  getAvailableLocales(): Array<{ code: string; name: string }> {
    return i18n.getAvailableLanguages();
  }

  // ============================================================
  // DOM Interaction Methods
  // ============================================================

  /**
   * Bind click handler to element by ID
   */
  bindAction(elementId: string, handler: () => void | Promise<void>): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          await handler();
        } catch (error) {
          this.handleError(error instanceof Error ? error : new Error(String(error)), 'action');
        }
      });
    } else {
      this.log(`Element not found: ${elementId}`);
    }
  }

  /**
   * Show a specific screen by ID
   */
  showScreen(screenName: ScreenName): void {
    // Hide all screens
    this.screenElements.forEach((element, name) => {
      if (name === screenName) {
        element.classList.remove('hidden');
        element.classList.add('flex');
      } else {
        element.classList.add('hidden');
        element.classList.remove('flex');
      }
    });

    this.currentScreen = screenName;
  }

  /**
   * Update progress indicator
   */
  updateProgress(step: number, total: number): void {
    if (this.config.progressElement) {
      const percent = Math.round((step / total) * 100);
      this.config.progressElement.textContent = `Step ${step} of ${total}`;
      this.config.progressElement.setAttribute('data-progress', String(percent));
    }
  }

  /**
   * Show status message
   */
  showStatus(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    if (this.config.statusElement) {
      const el = this.config.statusElement;
      el.textContent = message;

      // Remove previous status type classes
      el.classList.remove(
        'status-info',
        'status-success',
        'status-warning',
        'status-error',
        'bg-blue-100',
        'text-blue-800',
        'bg-green-100',
        'text-green-800',
        'bg-yellow-100',
        'text-yellow-800',
        'bg-red-100',
        'text-red-800',
        'hidden'
      );

      // Add new status type classes (support both custom and Tailwind)
      el.classList.add(`status-${type}`);

      // Add Tailwind color classes based on type
      switch (type) {
        case 'success':
          el.classList.add('bg-green-100', 'text-green-800');
          break;
        case 'warning':
          el.classList.add('bg-yellow-100', 'text-yellow-800');
          break;
        case 'error':
          el.classList.add('bg-red-100', 'text-red-800');
          break;
        default:
          el.classList.add('bg-blue-100', 'text-blue-800');
      }
    }
    this.log(`[${type}] ${message}`);
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private parseUrlParams(): URLParams {
    const params = new URLSearchParams(window.location.search);

    // Parse theme - validate against allowed values
    const themeParam = params.get('theme');
    let theme: 'light' | 'dark' | 'system' | null = null;
    if (themeParam === 'light' || themeParam === 'dark' || themeParam === 'system') {
      theme = themeParam;
    }

    // Check for explicit demo mode (?demo=true or ?demo=1)
    const demoParam = params.get('demo');
    const isExplicitDemo = demoParam === 'true' || demoParam === '1';

    // Determine mode - explicit demo param takes precedence
    let mode = params.get('mode');
    if (isExplicitDemo) {
      mode = 'demo';
    }

    // In demo mode, use 'demo' as apiKey if not provided
    let apiKey = params.get('api_key');
    if (mode === 'demo' && !apiKey) {
      apiKey = 'demo';
    }

    return {
      apiKey,
      callbackUrl: params.get('callback_url'),
      mode,
      userId: params.get('user_id'),
      theme,
      locale: params.get('locale'),
    };
  }

  private discoverScreenElements(): void {
    // Find all elements with data-screen attribute
    const screens = this.config.screenContainer.querySelectorAll('[data-screen]');
    screens.forEach((element) => {
      const screenName = element.getAttribute('data-screen') as ScreenName;
      if (screenName) {
        this.screenElements.set(screenName, element as HTMLElement);
      }
    });

    this.log(`Discovered ${this.screenElements.size} screens`);
  }

  private handleScreenChange(screen: ScreenConfig, prevScreen?: ScreenName): void {
    this.showScreen(screen.name);

    // Update progress if available
    if (screen.showProgress && screen.progressStep && screen.totalSteps) {
      this.updateProgress(screen.progressStep, screen.totalSteps);
    }

    // Show/hide camera based on screen config
    if (screen.showCamera) {
      this.config.videoElement.classList.remove('hidden');
    } else {
      this.config.videoElement.classList.add('hidden');
    }

    // Show/hide overlay based on screen config
    if (this.config.overlayCanvas) {
      if (screen.showOverlay) {
        this.config.overlayCanvas.classList.remove('hidden');
      } else {
        this.config.overlayCanvas.classList.add('hidden');
      }
    }

    this.log(`Screen changed: ${prevScreen || 'none'} -> ${screen.name}`);
  }

  private handleVerificationComplete(result: VerificationResult): void {
    if (result.success) {
      // Generate verification token and redirect
      const token = result.sessionId || 'unknown';
      this.redirectToCallback('success', token);
    } else {
      this.showStatus(result.errors?.[0] || 'Verification failed', 'error');
    }
  }

  private handleError(error: Error, context?: string): void {
    this.showStatus(error.message, 'error');
    this.log(`Error [${context}]:`, error);
  }

  private handleFaceDetection(detected: boolean, quality?: SceneQualityResult): void {
    // Update video element classes for CSS styling
    const videoElement = this.config.videoElement;

    if (detected) {
      videoElement.classList.remove('face-not-detected');
      videoElement.classList.add('face-detected');
    } else {
      videoElement.classList.add('face-not-detected');
      videoElement.classList.remove('face-detected');
    }

    // Forward to external callbacks — host app handles status display
    // (screen-aware, so it only shows face feedback on the face detection screen)
    this.callbacks.onFaceDetected?.(detected, quality);
  }

  private redirectToCallback(status: 'success' | 'failed' | 'cancelled', token?: string): void {
    if (!this.urlParams.callbackUrl) {
      this.log('No callback URL provided, staying on page');
      return;
    }

    // Validate callback URL for security (prevent open redirect attacks)
    const validationResult = this.validateCallbackUrl(this.urlParams.callbackUrl);
    if (!validationResult.valid) {
      const errorMsg = `Security: Blocked redirect to untrusted URL. ${validationResult.reason}`;
      this.log(errorMsg);
      this.callbacks.onError?.(new Error(errorMsg), 'redirect');
      return;
    }

    const url = new URL(this.urlParams.callbackUrl);
    url.searchParams.set('status', status);
    if (token) {
      url.searchParams.set('token', token);
    }

    // IMPORTANT: Include session_id and user_id for external SDK's autoStoreFromCallback()
    // The external SDK uses these to fetch verification result from API
    if (this.sessionId) {
      url.searchParams.set('session_id', this.sessionId);
    }
    if (this.urlParams.userId) {
      url.searchParams.set('user_id', this.urlParams.userId);
    }

    this.log(`Redirecting to: ${url.toString()}`);
    window.location.href = url.toString();
  }

  /**
   * Validates callback URL for security
   * Prevents open redirect attacks by ensuring URLs are from trusted origins
   */
  private validateCallbackUrl(callbackUrl: string): { valid: boolean; reason?: string } {
    let url: URL;
    try {
      url = new URL(callbackUrl);
    } catch {
      return { valid: false, reason: 'Invalid URL format' };
    }

    const protocol = url.protocol.toLowerCase();
    const hostname = url.hostname.toLowerCase();

    // Always allow localhost in development (unless explicitly disabled)
    const allowLocalhost = this.config.allowLocalhostCallbacks !== false;
    const isLocalhost =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

    if (isLocalhost && allowLocalhost) {
      return { valid: true };
    }

    // Require HTTPS for non-localhost
    if (protocol !== 'https:') {
      return { valid: false, reason: `Non-HTTPS protocol not allowed: ${protocol}` };
    }

    // If allowedCallbackOrigins is specified, validate against whitelist
    const allowedOrigins = this.config.allowedCallbackOrigins;
    if (allowedOrigins && allowedOrigins.length > 0) {
      const isAllowed = allowedOrigins.some((pattern) =>
        this.matchesOriginPattern(hostname, pattern)
      );
      if (!isAllowed) {
        return { valid: false, reason: `Origin not in allowlist: ${hostname}` };
      }
    }

    return { valid: true };
  }

  /**
   * Matches hostname against an origin pattern (supports wildcards)
   * @example '*.example.com' matches 'app.example.com', 'sub.app.example.com'
   * @example 'example.com' matches only 'example.com'
   */
  private matchesOriginPattern(hostname: string, pattern: string): boolean {
    // Normalize to lowercase for case-insensitive comparison
    const lowerHostname = hostname.toLowerCase();

    // Remove protocol if present in pattern
    const cleanPattern = pattern.replace(/^https?:\/\//, '').toLowerCase();

    // Handle wildcard patterns
    if (cleanPattern.startsWith('*.')) {
      const baseDomain = cleanPattern.slice(2);
      // Match the base domain itself or any subdomain
      return lowerHostname === baseDomain || lowerHostname.endsWith('.' + baseDomain);
    }

    // Exact match
    return lowerHostname === cleanPattern;
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log('[VerificationController]', ...args);
    }
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create and initialize a VerificationController
 */
export async function createVerificationController(
  config: VerificationControllerConfig,
  callbacks?: VerificationControllerCallbacks
): Promise<VerificationController> {
  const controller = new VerificationController(config);

  if (callbacks) {
    controller.setCallbacks(callbacks);
  }

  await controller.initialize();

  // IMPORTANT: Reference methods that are called from external code (index.html)
  // to prevent esbuild from tree-shaking them as "unused"
  void controller.getLivenessResult;
  void controller.resetLiveness;

  return controller;
}

// ============================================================
// Auto-initialization Helper
// ============================================================

/**
 * Auto-initialize controller on DOMContentLoaded.
 * This is the entry point when loaded as a script in the browser.
 */
export function autoInitialize(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('DOMContentLoaded', async () => {
    // Look for required elements
    const videoElement = document.getElementById('camera-feed') as HTMLVideoElement;
    const overlayCanvas = document.getElementById('camera-overlay') as HTMLCanvasElement;
    const screenContainer = document.getElementById('screen-container') as HTMLElement;
    const statusElement = document.getElementById('status-message') as HTMLElement;
    const progressElement = document.getElementById('progress-indicator') as HTMLElement;

    if (!videoElement) {
      console.error('[VerificationController] Missing required element: #camera-feed');
      return;
    }

    if (!screenContainer) {
      console.error('[VerificationController] Missing required element: #screen-container');
      return;
    }

    try {
      const controller = await createVerificationController({
        videoElement,
        overlayCanvas,
        screenContainer,
        statusElement,
        progressElement,
        debug: true,
      });

      // Expose controller globally for debugging and button handlers
      window.AppBouncerController = controller;

      // Bind common actions
      controller.bindAction('btn-start', () => controller.start());
      controller.bindAction('btn-next', () => controller.next());
      controller.bindAction('btn-back', () => controller.back());
      controller.bindAction('btn-cancel', () => controller.cancel());

      console.log('[VerificationController] Initialized successfully');
    } catch (error) {
      console.error('[VerificationController] Initialization failed:', error);
    }
  });
}
